import { useState, useMemo, useCallback } from 'react';
import type { PlanCell } from '../types/plan';
import {
  buildEntityLookup,
  findEntityByCell,
  getEntityCellKeys,
  type PlanEntity,
  type EntityLookup,
} from '../utils/planEntities';

export interface EntityInteractionState {
  hoveredEntity: PlanEntity | null;
  selectedEntity: PlanEntity | null;
  hoveredCellKeys: Set<string>;
  selectedCellKeys: Set<string>;
}

export interface EntityInteractionHandlers {
  handleCellHover: (row: number, col: number) => void;
  handleCellHoverEnd: () => void;
  handleCellClick: (row: number, col: number) => void;
  clearSelection: () => void;
}

/**
 * Hook for managing entity-based interactions (hover, select)
 * Provides optimized lookup and highlighting for multi-cell entities
 */
export const useEntityInteraction = (
  cells: PlanCell[],
  enabled: boolean = true
): [EntityInteractionState, EntityInteractionHandlers, EntityLookup] => {
  const [hoveredEntity, setHoveredEntity] = useState<PlanEntity | null>(null);
  const [selectedEntity, setSelectedEntity] = useState<PlanEntity | null>(null);

  // Build entity lookup map (memoized)
  const entityLookup = useMemo(() => buildEntityLookup(cells), [cells]);

  // Get cell keys for hovered entity
  const hoveredCellKeys = useMemo(() => {
    if (!hoveredEntity) return new Set<string>();
    return getEntityCellKeys(hoveredEntity);
  }, [hoveredEntity]);

  // Get cell keys for selected entity
  const selectedCellKeys = useMemo(() => {
    if (!selectedEntity) return new Set<string>();
    return getEntityCellKeys(selectedEntity);
  }, [selectedEntity]);

  // Handle cell hover
  const handleCellHover = useCallback(
    (row: number, col: number) => {
      if (!enabled) return;
      const entity = findEntityByCell(row, col, entityLookup);
      setHoveredEntity(entity);
    },
    [entityLookup, enabled]
  );

  // Handle hover end
  const handleCellHoverEnd = useCallback(() => {
    setHoveredEntity(null);
  }, []);

  // Handle cell click (select entity)
  const handleCellClick = useCallback(
    (row: number, col: number) => {
      if (!enabled) return;
      const entity = findEntityByCell(row, col, entityLookup);
      
      // Toggle selection: if clicking the same entity, deselect it
      if (selectedEntity === entity) {
        setSelectedEntity(null);
      } else {
        setSelectedEntity(entity);
      }
    },
    [entityLookup, selectedEntity, enabled]
  );

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedEntity(null);
  }, []);

  const state: EntityInteractionState = {
    hoveredEntity,
    selectedEntity,
    hoveredCellKeys,
    selectedCellKeys,
  };

  const handlers: EntityInteractionHandlers = {
    handleCellHover,
    handleCellHoverEnd,
    handleCellClick,
    clearSelection,
  };

  return [state, handlers, entityLookup];
};
